import React, { useState, useEffect } from "react";
import "./index.css";

const ArticleTableOfContents = ({ content }) => {
  const [activeId, setActiveId] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Extract headings from content
  let headingIndex = 0;
  let faqIndex = 0;

  const headings = content.flatMap((item, contentIndex) => {
    // Regular headings
    if (
      item.__typename === "WordpressWpPost_ContentPost_Content_Heading" &&
      (item.level === "level1" ||
        item.level === "level2" ||
        item.level === "level3")
    ) {
      const id = `heading-${headingIndex++}`;
      return [
        {
          id: id,
          text: item.text,
          level: item.level,
          type: "heading"
        }
      ];
    }

    // FAQ sections
    if (
      item.__typename === "WordpressWpPost_ContentPost_Content_Faq" &&
      item.faq &&
      item.faq.length > 0
    ) {
      const faqSectionId = `faq-section-${faqIndex}`;
      const faqEntries = [
        {
          id: faqSectionId,
          text: "Frequently Asked Questions",
          level: "level2",
          type: "faq-section"
        }
      ];

      // Add individual FAQ questions as level3
      item.faq.forEach((faqItem, faqItemIndex) => {
        faqEntries.push({
          id: `faq-${faqIndex}-${faqItemIndex}`,
          text: faqItem.question,
          level: "level3",
          type: "faq-question"
        });
      });

      faqIndex++;
      return faqEntries;
    }

    return [];
  });

  // Generate hierarchical numbering
  const generateNumbering = () => {
    let h2Counter = 0;
    let h3Counter = 0;

    return headings.map(heading => {
      let number = "";

      if (heading.level === "level2") {
        h2Counter++;
        h3Counter = 0; // Reset H3 counter when new H2 starts
        number = `${h2Counter}.`;
      } else if (heading.level === "level3") {
        h3Counter++;
        number = `${h2Counter}.${h3Counter}`;
      }

      return {
        ...heading,
        number: number
      };
    });
  };

  const numberedHeadings = generateNumbering();

  // If no headings, don't render
  if (headings.length === 0) return null;

  // Smooth scroll to heading
  const scrollToHeading = id => {
    const element = document.getElementById(id);
    if (element) {
      // Adjust offset based on screen size - mobile needs more offset
      const offset = window.innerWidth <= 768 ? 600 : 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
      setActiveId(id);

      // Close mobile TOC after clicking
      if (window.innerWidth <= 768) {
        setIsCollapsed(true);
      }
    }
  };

  // Track scroll position to highlight active heading
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;

      // Find active heading
      for (let i = numberedHeadings.length - 1; i >= 0; i--) {
        const heading = document.getElementById(numberedHeadings[i].id);
        if (heading && heading.offsetTop <= scrollPosition) {
          setActiveId(numberedHeadings[i].id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [numberedHeadings]);

  return (
    <div className="article-toc-wrapper">
      {/* Mobile Toggle Button */}
      <button
        className="article-toc-toggle"
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-label="Toggle table of contents"
      >
        <span className="toc-toggle-text">TABLE OF CONTENTS</span>
        <svg
          className={`toc-toggle-icon ${isCollapsed ? "" : "open"}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Table of Contents */}
      <div
        className={`article-toc-container ${
          isCollapsed ? "collapsed" : "expanded"
        }`}
      >
        <div className="article-toc">
          <h3 className="article-toc-title">Table of Contents</h3>
          <ul className="article-toc-list">
            {numberedHeadings.map(heading => (
              <li
                key={heading.id}
                className={`article-toc-item ${
                  heading.level === "level3"
                    ? "level-3"
                    : heading.level === "level2"
                    ? "level-2"
                    : "level-1"
                } ${activeId === heading.id ? "active" : ""}`}
              >
                <a
                  href={`#${heading.id}`}
                  onClick={e => {
                    e.preventDefault();
                    scrollToHeading(heading.id);
                  }}
                  className="article-toc-link"
                >
                  {heading.number && (
                    <span className="toc-number">{heading.number} </span>
                  )}
                  {heading.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ArticleTableOfContents;
